import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import {
    Alert,
    Button,
    Chip,
    Container,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';

import { Loading } from '../shared';

import { useEmailAudits } from '../hooks/emailAudit';

import { RouteTo } from '../routes';

import { STATUS_COLORS } from './format';

const UUID_V4_PATTERN =
    '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89aAbB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}';

export function EmailAuditList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const submittedEmail = searchParams.get('email') || undefined;
    const submittedOrderId = searchParams.get('orderId') || undefined;
    const [page, setPage] = useState(0);
    const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([
        undefined,
    ]);
    const cursor = pageCursors[page];
    const { emailAudits, nextCursor, isLoading, error } = useEmailAudits({
        email: submittedEmail,
        orderId: submittedOrderId,
        cursor,
    });
    const theme = useTheme();

    useEffect(() => {
        setPage(0);
        setPageCursors([undefined]);
    }, [submittedEmail, submittedOrderId]);

    if (isLoading) {
        return <Loading />;
    }

    const headerCellSx = {
        background: theme.palette.grey.A700,
        color: theme.palette.common.white,
    };

    return (
        <Container>
            <Stack sx={{ gap: 2, my: 2 }}>
                <div>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Email Audit
                    </Typography>
                </div>
                <Stack
                    component="form"
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    onSubmit={(event) => {
                        event.preventDefault();
                        const formData = new FormData(event.currentTarget);
                        const trimmedEmail = String(
                            formData.get('email') ?? '',
                        ).trim();
                        const trimmedOrderId = String(
                            formData.get('orderId') ?? '',
                        ).trim();

                        setSearchParams((currentParams) => {
                            const nextParams = new URLSearchParams(
                                currentParams,
                            );

                            if (trimmedEmail) {
                                nextParams.set('email', trimmedEmail);
                            } else {
                                nextParams.delete('email');
                            }

                            if (trimmedOrderId) {
                                nextParams.set('orderId', trimmedOrderId);
                            } else {
                                nextParams.delete('orderId');
                            }

                            return nextParams;
                        });
                    }}
                >
                    <TextField
                        label="Recipient email"
                        type="email"
                        size="small"
                        name="email"
                        defaultValue={submittedEmail ?? ''}
                    />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ alignSelf: 'center' }}
                    >
                        and/or
                    </Typography>
                    <TextField
                        label="Order ID"
                        size="small"
                        name="orderId"
                        defaultValue={submittedOrderId ?? ''}
                        slotProps={{
                            htmlInput: {
                                pattern: UUID_V4_PATTERN,
                                title: 'Enter a valid order ID (UUID v4)',
                            },
                        }}
                    />
                    <Button type="submit" variant="contained">
                        Search
                    </Button>
                </Stack>
                {error && (
                    <Alert severity="error">Unable to load email audits.</Alert>
                )}
                {!error && emailAudits.length === 0 ? (
                    <Typography color="text.secondary">
                        No email audits found.
                    </Typography>
                ) : (
                    <Paper>
                        <TableContainer>
                            <Table
                                size="small"
                                className="no-highlight"
                                sx={{
                                    width: '100%',
                                    tableLayout: 'fixed',
                                    '& .MuiTableCell-root': {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headerCellSx}>
                                            Subject
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            Recipient
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            Template
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            Order ID
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            Status
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            Created At
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {emailAudits.map((audit) => (
                                        <TableRow
                                            key={audit.postmarkMessageId}
                                            hover
                                        >
                                            <TableCell
                                                sx={{
                                                    maxWidth: 360,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Link
                                                    component={RouterLink}
                                                    to={RouteTo.EmailAuditItem(
                                                        audit.postmarkMessageId,
                                                    )}
                                                    title={audit.subject}
                                                >
                                                    {audit.subject}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    title={audit.recipientEmail}
                                                >
                                                    {audit.recipientEmail}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    title={audit.emailTemplate}
                                                >
                                                    {audit.emailTemplate}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: 12,
                                                }}
                                            >
                                                <span title={audit.orderId}>
                                                    {audit.orderId}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={
                                                        STATUS_COLORS[
                                                            audit.deliveryStatus
                                                        ] ?? 'default'
                                                    }
                                                    label={audit.deliveryStatus}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <span title={audit.createdAt}>
                                                    {audit.createdAt}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                p: 1,
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="body2">
                                Page {page + 1}
                            </Typography>
                            <Button
                                disabled={page === 0}
                                onClick={() =>
                                    setPage((current) => current - 1)
                                }
                            >
                                Previous
                            </Button>
                            <Button
                                disabled={!nextCursor}
                                onClick={() => {
                                    if (!nextCursor) {
                                        return;
                                    }
                                    setPageCursors((cursors) => [
                                        ...cursors.slice(0, page + 1),
                                        nextCursor,
                                    ]);
                                    setPage((current) => current + 1);
                                }}
                            >
                                Next
                            </Button>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
}
